import {
  AccountInfo,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser';

export interface GatewayConfig {
  tenantId: string;
  clientId: string;
  scope: string;
  baseUrl: string;
  title: string;
}

let msal: PublicClientApplication | null = null;
let config: GatewayConfig | null = null;

/**
 * The sign-in settings come from the gateway at runtime, not from the build.
 * That way the same artefact runs in every environment.
 */
export async function initialiseAuth(): Promise<GatewayConfig> {
  const response = await fetch('/api/public/config');
  if (!response.ok) throw new Error('The configuration of the gateway is unreachable.');
  config = (await response.json()) as GatewayConfig;

  msal = new PublicClientApplication({
    auth: {
      clientId: config.clientId,
      authority: `https://login.microsoftonline.com/${config.tenantId}`,
      redirectUri: window.location.origin,
    },
    cache: { cacheLocation: 'sessionStorage', storeAuthStateInCookie: false },
  });

  await msal.initialize();
  await msal.handleRedirectPromise();
  return config;
}

export function currentAccount(): AccountInfo | null {
  return msal?.getAllAccounts()[0] ?? null;
}

export async function signIn(): Promise<void> {
  if (!msal || !config) throw new Error('Sign-in is not initialised.');
  await msal.loginRedirect({ scopes: [config.scope] });
}

export async function signOut(): Promise<void> {
  await msal?.logoutRedirect();
}

/**
 * A token for the admin API. First silently from the cache, and only when
 * Entra demands an interaction, via a redirect.
 */
export async function acquireToken(): Promise<string> {
  if (!msal || !config) throw new Error('Sign-in is not initialised.');
  const account = currentAccount();
  if (!account) throw new Error('Not signed in.');

  try {
    const result = await msal.acquireTokenSilent({ scopes: [config.scope], account });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msal.acquireTokenRedirect({ scopes: [config.scope], account });
    }
    throw error;
  }
}

export function gatewayConfig(): GatewayConfig {
  if (!config) throw new Error('Configuration not loaded.');
  return config;
}
